import { useEffect, useRef, useState, useMemo, memo } from "react";

import debounce from "lodash/debounce";

import { TagType } from "@/graphql";

interface DynamicTagListProps {
  tags: TagType[];
  containerClassName?: string;
}

const SingleTag = memo(({ tag }: { tag: TagType }) => {
  return (
    <div className="font-inter inline-flex items-center justify-center rounded bg-[#E9F1FB] px-2 py-1 text-sm text-[#778397]">
      {tag.name}
    </div>
  );
});

SingleTag.displayName = "SingleTag";

function DynamicTagList({
  tags,
  containerClassName = "",
}: DynamicTagListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tempDiv = useRef<HTMLDivElement | null>(null);
  const tagWidthCache = useRef<Map<string, number>>(new Map());
  const [visibleCount, setVisibleCount] = useState(tags.length);

  const visibleTags = useMemo(
    () => tags.slice(0, visibleCount),
    [tags, visibleCount],
  );
  const hiddenCount = tags.length - visibleCount;

  useEffect(() => {
    // Create temporary div once
    tempDiv.current = document.createElement("div");
    tempDiv.current.style.visibility = "hidden";
    tempDiv.current.style.position = "absolute";
    document.body.appendChild(tempDiv.current);

    return () => {
      if (tempDiv.current) {
        document.body.removeChild(tempDiv.current);
      }
    };
  }, []);

  useEffect(() => {
    const calculateVisibleTags = () => {
      if (!containerRef.current || !tempDiv.current) return;

      const container = containerRef.current;
      const containerWidth = container.offsetWidth;
      let totalWidth = 0;
      let calculatedVisibleCount = 0;

      // Test each tag to see how many fit
      for (let i = 0; i < tags.length; i++) {
        const tag = tags[i];
        let tagWidth = tagWidthCache.current.get(tag.name);

        if (tagWidth === undefined) {
          tempDiv.current.innerHTML = `
            <div class="inline-flex items-center justify-center px-2 py-1 bg-[#E9F1FB] rounded text-sm">
              ${tag.name}
            </div>
          `;
          tagWidth =
            tempDiv.current.firstElementChild?.getBoundingClientRect().width ||
            0;
          tagWidthCache.current.set(tag.name, tagWidth);
        }

        // Add margin between tags
        totalWidth += tagWidth + 8; // 8px gap

        // Check if we need space for the "+N more" tag
        const moreTagSpace = i < tags.length - 1 ? 80 : 0; // Approximate width for "+N more"

        if (totalWidth + moreTagSpace <= containerWidth) {
          calculatedVisibleCount++;
        } else {
          break;
        }
      }

      setVisibleCount(calculatedVisibleCount);
    };

    const debouncedCalculation = debounce(calculateVisibleTags, 150);

    // Initial calculation
    calculateVisibleTags();

    // Add resize observer with debounced calculation
    const resizeObserver = new ResizeObserver(debouncedCalculation);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      debouncedCalculation.cancel();
    };
  }, [tags]);

  return (
    <div
      ref={containerRef}
      className={`flex flex-wrap items-center gap-2 ${containerClassName}`}
    >
      {visibleTags.map((tag) => (
        <SingleTag key={tag.id} tag={tag} />
      ))}
      {hiddenCount > 0 && (
        <span className="cursor-pointer text-sm font-medium text-[#778397]">
          +{hiddenCount} Tags
        </span>
      )}
    </div>
  );
}

export default memo(DynamicTagList);
